import '@babylonjs/loaders/glTF';
import { Quaternion, SceneLoader, Vector3, type Animation, type AnimationGroup, type AssetContainer, type Scene, type TransformNode } from '@babylonjs/core';
import type { LoadedCharacterMesh } from './character-mesh';

/**
 * The one bone whose translation a clip really animates: the pelvis carries
 * the body's bob, lunge and fall. Every other bone's translation in the base
 * is just the base rig's own bone length, held constant.
 */
const MOTION_BONE_NAME = 'Pelvis';

/**
 * The joints whose rotations carry over relative to rest (see
 * `cloneRetargetedAnimationGroups`): the legs, where a creature's build
 * differs most (a wolf's bent knees and raised heels). The rest copy the
 * clip as it is; a creature's resting lean added to a clip's own lean folds
 * it over.
 */
const REST_RELATIVE_JOINT_PATTERN = /^(Hip|Knee|Foot|Toe)\./;

export interface AnimationBase {
    readonly animationGroups: readonly AnimationGroup[];
    /**
     * Rest distance of the base rig's pelvis from its parent, the yardstick a
     * character's own pelvis is measured against to scale the body motion.
     * Undefined when the base has no pelvis, which leaves the motion unscaled.
     */
    readonly motionBoneRestLength?: number;
    /**
     * Each base joint's rest rotation, by joint name: what a clip's rotations
     * are measured from when they are carried onto a character with a
     * different rest pose (see `cloneRetargetedAnimationGroups`).
     */
    readonly restRotations?: ReadonlyMap<string, Quaternion>;
}

/** The parts of an `AnimationBase` that retargeting reads. */
export type AnimationBaseRest = Pick<AnimationBase, 'motionBoneRestLength' | 'restRotations'>;

/** Each node's current local rotation, by name: its rest rotation, read before anything animates it. */
export function captureRestRotations(nodes: readonly TransformNode[]): Map<string, Quaternion> {
    return new Map(nodes.map((node) => [node.name, localRotation(node)]));
}

function localRotation(node: TransformNode): Quaternion {
    return node.rotationQuaternion?.clone() ?? Quaternion.FromEulerVector(node.rotation);
}

/** Rest distance of the pelvis among `nodes` from its parent, if there is one. */
export function measureMotionBoneRestLength(nodes: readonly TransformNode[]): number | undefined {
    const length = nodes.find((node) => node.name === MOTION_BONE_NAME)?.position.length();
    return length !== undefined && length > 0 ? length : undefined;
}

/**
 * Loads a shared, mesh-free animation base (a skeleton + baked animation
 * groups, exported from Mixamo downloads taken "without skin") into `scene`.
 * `rootUrl` + `fileName` follow Babylon's own `SceneLoader` split, same as
 * `loadCharacterMesh`.
 *
 * Uses `LoadAssetContainerAsync` rather than `ImportMeshAsync`: a mesh-free
 * glTF has no skin to import, and `ImportMeshAsync` (built around producing
 * meshes) throws on that. The asset pipeline (`merge-animations.py`) also
 * bakes in a placeholder mesh so glTF exporters that refuse to emit a
 * skeleton with zero skinned meshes still keep the joint hierarchy - that
 * placeholder is discarded here since only the animation groups are wanted.
 *
 * Returns just `animationGroups`; the base's own joint TransformNodes are
 * intentionally not exposed - each animation group's `targetedAnimations`
 * still reference them until `retargetAnimationsToCharacter` repoints them
 * at a real character's joints.
 */
export async function loadAnimationBase(scene: Scene, rootUrl: string, fileName: string): Promise<AnimationBase> {
    const container = await SceneLoader.LoadAssetContainerAsync(rootUrl, fileName, scene);
    if (container.animationGroups.length === 0) {
        throw new Error(`loadAnimationBase: ${rootUrl}${fileName} produced zero animation groups.`);
    }
    return createAnimationBase(container);
}

/**
 * The animation base in a loaded container: its animation groups and its
 * pelvis rest length, with the pipeline's placeholder mesh (and anything
 * else meshy) discarded. The animation groups keep targeting the joints.
 *
 * The pelvis is measured first: the skeleton hangs under the glTF
 * `__root__` mesh, so disposing the meshes disposes the joints, which
 * removes them from `container.transformNodes`.
 */
export function createAnimationBase(container: Pick<AssetContainer, 'animationGroups' | 'meshes' | 'transformNodes'>): AnimationBase {
    const motionBoneRestLength = measureMotionBoneRestLength(container.transformNodes);
    const restRotations = captureRestRotations(container.transformNodes);
    for (const mesh of [...container.meshes]) {
        mesh.dispose();
    }
    const animationGroups = container.animationGroups;
    return motionBoneRestLength !== undefined
        ? { animationGroups, motionBoneRestLength, restRotations }
        : { animationGroups, restRotations };
}

/**
 * Repoints every animation group's targeted-node animations from the
 * animation base's own joint hierarchy onto `character`'s equivalent
 * joints, matched by exact node name. Required because `loadAnimationBase`
 * and `loadCharacterMesh` are two independent glTF imports - their skin
 * joints are different TransformNode instances even when the source rigs
 * are identical (same Mixamo bone names/order), so an AnimationGroup loaded
 * from one import does nothing when played against the other's mesh until
 * retargeted.
 *
 * Any targeted animation whose base joint name has no equivalent on
 * `character` (e.g. finger/end bones present in the animation base's
 * source rig but absent from a reduced-bone character mesh) is left
 * pointing at the (now-detached) base joint - Babylon evaluates it but it
 * drives nothing visible, so this is silent and expected, not an error.
 *
 * Mutates the given animation groups in place. Call once per
 * (animationGroups, character) pairing - calling again on a group already
 * retargeted to a different character re-points it again, making it
 * exclusive to the new one.
 */
export function retargetAnimationsToCharacter(
    animationGroups: readonly AnimationGroup[],
    character: LoadedCharacterMesh,
): void {
    const nodesByName = new Map(character.transformNodes.map((node) => [node.name, node]));

    for (const group of animationGroups) {
        for (const targetedAnimation of group.targetedAnimations) {
            const targetName: string | undefined = targetedAnimation.target?.name;
            const node = targetName ? nodesByName.get(targetName) : undefined;
            if (node) {
                targetedAnimation.target = node;
            }
        }
    }
}

/**
 * Same retargeting as `retargetAnimationsToCharacter`, but non-destructive:
 * returns freshly-cloned animation groups pointed at `character`'s joints,
 * leaving `animationGroups` (and the `Animation` keyframe data every clone
 * references, per `clone`'s `cloneAnimations = false` default) untouched.
 *
 * Required once a skeleton type's animation base is loaded and cached once
 * per session (`CharacterLoader.loadAnimationBase`) rather than re-parsed
 * from the source glTF for every entity that shares it - mutating the
 * shared master in place (the older `retargetAnimationsToCharacter`
 * function above) would repoint it to whichever entity retargeted it last,
 * breaking every other entity sharing the same cached base. Each clone owns
 * its own `TargetedAnimation` wrappers (cheap: no keyframe data is
 * duplicated) so entities can safely dispose their own clones independently
 * without affecting the shared master or any other entity's clone.
 *
 * Clones keep the base's exact group name (e.g. "idle", "attack:melee.sword.1")
 * rather than a per-entity-prefixed one: `playCharacterAnimation`/
 * `AnimationVariantPlayer` look up an entity's animation group by exact
 * name match against its own `animationGroups` array, using the vocab's
 * unprefixed names - each entity has its own separate array, so name
 * collisions across entities are harmless, but a prefixed name breaks every
 * lookup for every entity (confirmed live: this exact prefixing shipped
 * once and silently stopped all animation playback).
 *
 * Clones also keep the character's own proportions. Only rotations carry
 * over to every joint; the base's translation and scale tracks would stamp
 * the base rig's bone lengths onto every joint, so a short creature
 * stretches to the base's height and a tall one shrinks. They are dropped,
 * except the pelvis translation, which carries the body motion. That track
 * is scaled by the character's pelvis rest length over the base's
 * (`base.motionBoneRestLength`), so a crouch or a fall travels the
 * character's own distance. Scaled tracks are cached per base track and
 * scale, so every character of one height shares one copy.
 *
 * And they keep the shape of its legs. A leg joint's rotation is carried
 * over as how far the clip turns it from the base rig's rest
 * (`base.restRotations`), applied on top of the character's own rest:
 * `characterRest * inverse(baseRest) * clipRotation`. Copied as they are,
 * the base rig's human angles straightened a wolf's bent legs. Other joints
 * copy the clip as it is (see `REST_RELATIVE_JOINT_PATTERN`). A joint whose
 * rest matches the base's keeps the shared track; adjusted tracks are
 * cached per base track and rest. Bent legs stand shorter, so callers
 * ground the result (see `measureGroundOffset`).
 * The character's joints must be at rest when this runs (freshly loaded).
 */
export function cloneRetargetedAnimationGroups(
    animationGroups: readonly AnimationGroup[],
    character: LoadedCharacterMesh,
    base: AnimationBaseRest = {},
): AnimationGroup[] {
    const nodesByName = new Map(character.transformNodes.map((node) => [node.name, node]));
    const characterRestLength = measureMotionBoneRestLength(character.transformNodes);
    const motionScale = characterRestLength !== undefined && base.motionBoneRestLength !== undefined
        ? characterRestLength / base.motionBoneRestLength
        : 1;
    const characterRests = base.restRotations ? captureRestRotations(character.transformNodes) : undefined;

    return animationGroups.map((group) => {
        const clone = group.clone(group.name, (oldTarget: { name?: string } | null | undefined) => {
            const targetName = oldTarget?.name;
            return (targetName && nodesByName.get(targetName)) || oldTarget;
        });
        for (const targeted of [...clone.targetedAnimations]) {
            const property = targeted.animation.targetProperty;
            const target = targeted.target as { name?: string } | null;
            if (property === 'rotationQuaternion') {
                const restRelative = target?.name !== undefined && REST_RELATIVE_JOINT_PATTERN.test(target.name);
                const baseRest = restRelative ? base.restRotations?.get(target.name!) : undefined;
                const characterRest = target?.name !== undefined ? characterRests?.get(target.name) : undefined;
                if (baseRest && characterRest) {
                    const adjusted = restRelativeTrack(targeted.animation, baseRest, characterRest);
                    if (adjusted !== targeted.animation) {
                        clone.removeTargetedAnimation(targeted.animation);
                        clone.addTargetedAnimation(adjusted, target);
                    }
                }
                continue;
            }
            if (property !== 'position' && property !== 'scaling') {
                continue;
            }
            clone.removeTargetedAnimation(targeted.animation);
            if (property === 'position' && target?.name === MOTION_BONE_NAME) {
                clone.addTargetedAnimation(scaledMotionTrack(targeted.animation, motionScale), target);
            }
        }
        return clone;
    });
}

/** Scaled copies of base pelvis tracks, by scale rounded to `MOTION_SCALE_PRECISION`. */
const scaledMotionTracks = new WeakMap<Animation, Map<number, Animation>>();
const MOTION_SCALE_PRECISION = 1000;

/** `animation` with every Vector3 key scaled by `scale`; the shared original when the scale is 1. */
function scaledMotionTrack(animation: Animation, scale: number): Animation {
    const roundedScale = Math.round(scale * MOTION_SCALE_PRECISION) / MOTION_SCALE_PRECISION;
    if (roundedScale === 1) {
        return animation;
    }
    let byScale = scaledMotionTracks.get(animation);
    if (!byScale) {
        byScale = new Map();
        scaledMotionTracks.set(animation, byScale);
    }
    const cached = byScale.get(roundedScale);
    if (cached) {
        return cached;
    }
    const scaled = animation.clone();
    scaled.setKeys(animation.getKeys().map((key) => ({
        ...key,
        value: key.value instanceof Vector3 ? key.value.scale(roundedScale) : key.value,
    })));
    byScale.set(roundedScale, scaled);
    return scaled;
}

/** Rest-adjusted copies of base rotation tracks, by the rest correction they apply. */
const restRelativeTracks = new WeakMap<Animation, Map<string, Animation>>();
/** Rest corrections closer to identity than this are treated as none. */
const REST_MATCH_EPSILON = 1e-4;

/**
 * `animation` with every key q turned into `characterRest * inverse(baseRest) * q`;
 * the shared original when the two rests match.
 */
function restRelativeTrack(animation: Animation, baseRest: Quaternion, characterRest: Quaternion): Animation {
    const correction = characterRest.multiply(Quaternion.Inverse(baseRest)).normalize();
    if (1 - Math.abs(correction.w) < REST_MATCH_EPSILON) {
        return animation;
    }
    const key = correction.asArray().map((value) => value.toFixed(4)).join(',');
    let byCorrection = restRelativeTracks.get(animation);
    if (!byCorrection) {
        byCorrection = new Map();
        restRelativeTracks.set(animation, byCorrection);
    }
    const cached = byCorrection.get(key);
    if (cached) {
        return cached;
    }
    const adjusted = animation.clone();
    adjusted.setKeys(animation.getKeys().map((frame) => ({
        ...frame,
        value: frame.value instanceof Quaternion ? correction.multiply(frame.value) : frame.value,
    })));
    byCorrection.set(key, adjusted);
    return adjusted;
}
